import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi, ProjectApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatCurrencyItemTooltip,
    KpiCard,
    MetricHelpLabel,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

const SuspiciousDomainPatterns = [
    "facebook.com",
    "fb.com",
    "instagram.com",
    "google.com",
    "gopay.com",
    "seznam.cz",
    "localhost",
];

function isSuspiciousDomain(hostName) {
    const normalized = String(hostName ?? "").toLowerCase();
    return SuspiciousDomainPatterns.some((pattern) => normalized === pattern || normalized.endsWith(`.${pattern}`));
}

function domainName(domainWithStats) {
    return domainWithStats?.projectDomain?.hostName ?? "-";
}

export default function ReportingDomainsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [domains, setDomains] = useState([]);
    const [performanceByDomain, setPerformanceByDomain] = useState([]);
    const [loading, setLoading] = useState(true);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const eligibleDomains = useMemo(() => domains
        .filter((domain) => domain?.projectDomain?.isEnabled)
        .filter((domain) => (domainsAllowListed ?? []).length === 0 || domainsAllowListed.includes(domain?.projectDomain?.id))
        .sort((a, b) => domainName(a).localeCompare(domainName(b), "cs")), [domains, domainFilterKey]);
    const suspiciousDomains = useMemo(() => eligibleDomains.filter((domain) => isSuspiciousDomain(domainName(domain))), [eligibleDomains]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setDomains([]);
        setPerformanceByDomain([]);

        const authorityConfiguration = getAuthorityConfiguration();
        const projectApi = new ProjectApi(authorityConfiguration);
        const erpApi = new ErpCalculationApi(authorityConfiguration);

        Promise.all([
            projectApi.listProjectDomains({projectId: project.id}),
            erpApi.shopPerformanceByDomain({
                projectId: project.id,
                erpFilter: {
                    since: sinceString,
                    until: untilString,
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            }),
        ])
            .then(([nextDomains, dimensionData]) => {
                if (!active) {
                    return;
                }

                setDomains(nextDomains);
                const domainsById = new Map((nextDomains ?? []).map((domain) => [domain?.projectDomain?.id, domain]));
                setPerformanceByDomain((dimensionData?.rows ?? []).map((row) => ({
                    domain: domainsById.get(row.id),
                    id: row.id,
                    name: row.name,
                    performance: row.performance,
                })));
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, sinceString, untilString, centreFilterKey, domainFilterKey]);

    const rows = useMemo(() => performanceByDomain
        .map(({domain, name, id, performance}) => {
            const turnover = performance?.orderTurnoverGross;
            const profit = performance?.orderProfitGross;
            const hasProfit = profit != null;
            const marketing = performance?.orderSpendMarketing;
            const orders = Number(performance?.orderAmountGross ?? 0);

            return {
                id: domain?.projectDomain?.id ?? id,
                name: domainName(domain) === "-" ? name : domainName(domain),
                domain,
                turnover,
                ...(hasProfit ? {profit} : {}),
                marketing,
                orders,
                ...(hasProfit ? {margin: dividePercent(moneyAmount(profit), moneyAmount(turnover))} : {}),
                pno: dividePercent(moneyAmount(marketing), moneyAmount(turnover)),
                campaignCount: Number(domain?.projectDomainStats?.campaignCount ?? 0),
                adCount: Number(domain?.projectDomainStats?.adCount ?? 0),
                suspicious: isSuspiciousDomain(domainName(domain)),
            };
        })
        .sort((a, b) => moneyAmount(b.turnover) - moneyAmount(a.turnover)), [performanceByDomain]);

    const currency = rows.find((row) => row.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const totalTurnover = rows.reduce((sum, row) => sum + moneyAmount(row.turnover), 0);
    const totalProfit = rows.reduce((sum, row) => sum + moneyAmount(row.profit), 0);
    const hasProfit = rows.some((row) => row.profit != null);
    const totalOrders = rows.reduce((sum, row) => sum + row.orders, 0);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const turnoverOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...rows].slice(0, 10).reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 180, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: [...rows].slice(0, 10).reverse().map((row, index) => ({
                    value: moneyToMajor(row.turnover),
                    itemStyle: {color: ChannelMixColors[(rows.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 26,
            },
        ].filter((series) => hasProfit || series.name !== t("reporting_lab.comparison.margin", {defaultValue: "Marže"})),
    };

    const shareOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: rows.slice(0, 8).map((row) => ({
                    name: row.name,
                    value: moneyToMajor(row.turnover),
                })),
            },
        ],
    };

    const efficiencyOption = {
        color: ["#16a34a", "#7c3aed"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartPercent(value, 1)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: rows.slice(0, 10).map((row) => row.name),
            axisLabel: {fontSize: 12, width: 90, overflow: "truncate"},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartPercent(value)},
        },
        series: [
            {
                name: t("reporting_lab.comparison.margin", {defaultValue: "Marže"}),
                type: "bar",
                data: rows.slice(0, 10).map((row) => row.margin ?? 0),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.comparison.pno", {defaultValue: "PNO"}),
                type: "bar",
                data: rows.slice(0, 10).map((row) => row.pno ?? 0),
                barMaxWidth: 28,
            },
        ],
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.domains", {defaultValue: "Domény"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[suspiciousDomains.length > 0 ? {
            id: "suspicious-domains",
            type: "warning",
            showClose: false,
            title: t("reporting_lab.domains.audit_warning_title", {defaultValue: "Pozor na aktivní neprojektové domény"}),
            content: t("reporting_lab.domains.audit_warning", {defaultValue: "Tyto domény vypadají jako reklamní, platební nebo technické domény. Pokud jsou zapnuté jako reportované domény projektu, mohou zkreslovat filtry, statistiky i billing."}),
        } : null]}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.domains.title", {defaultValue: "K čemu slouží doménový přehled"})}
                >
                    {t("reporting_lab.descriptions.domains.text", {defaultValue: "Kontroluje výkon jednotlivých e-shopových domén v projektu a pomáhá odhalit doménový šum, který může zkreslovat reporting i fakturaci."})}
                </ReportingPageDescription>

                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.domains.active_domains", {defaultValue: "Aktivní domény"})} value={renderNumber(eligibleDomains.length)} meta={t("reporting_lab.domains.filtered_domains", {defaultValue: "Dle aktuálního filtru"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} value={renderCurrencyValue({amount: totalTurnover, currency}, true)} meta={t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"})}/>
                </div>
                {hasProfit && <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.profit", {defaultValue: "Marže"})} value={renderCurrencyValue({amount: totalProfit, currency}, true)} meta={renderPercentValue(dividePercent(totalProfit, totalTurnover))} tone={totalProfit < 0 ? "bad" : "default"}/>
                </div>}
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.domains.suspicious_domains", {defaultValue: "Podezřelé domény"})} value={renderNumber(suspiciousDomains.length)} meta={suspiciousDomains[0] ? domainName(suspiciousDomains[0]) : t("reporting_lab.domains.none", {defaultValue: "Žádné"})} emphasizeMeta={false} tone={suspiciousDomains.length > 0 ? "warn" : "default"}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.domains.revenue_by_domain", {defaultValue: "Obrat podle domény"})} className="col-span-12 xl:col-span-7">
                    <EChart option={turnoverOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.domains.revenue_share", {defaultValue: "Rozpad top 8 domén"})} className="col-span-12 xl:col-span-5">
                    <EChart option={shareOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.domains.efficiency", {defaultValue: "Efektivita domén"})} className="col-span-12">
                    <EChart option={efficiencyOption} className="h-80"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.domains.table", {defaultValue: "Přehled domén"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.domains.domain", {defaultValue: "Doména"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                {hasProfit && <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.table.contribution", {defaultValue: "Marže"})} tooltip={t("reporting_lab.table.contribution_help", {defaultValue: "Marže = obrat přiřazených objednávek minus nákupní cena zboží a marketingové náklady. Nejde o čistý účetní zisk; nezahrnuje interní náklady dopravy, plateb ani režii."})}/></th>}
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.comparison.pno", {defaultValue: "PNO"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.domains.campaigns", {defaultValue: "Kampaně"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.domains.ads", {defaultValue: "Reklamy"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {rows.map((row) => <tr key={row.id}>
                                <td className="py-3 pr-4">
                                    <div className="font-semibold text-slate-900 dark:text-slate-100">{row.name}</div>
                                    {row.suspicious && <div className="mt-1 text-xs font-semibold text-amber-600">{t("reporting_lab.domains.suspicious", {defaultValue: "Podezřelá doména"})}</div>}
                                </td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnover, true)}</td>
                                {hasProfit && <td className={`py-3 pr-4 text-right ${moneyAmount(row.profit) < 0 ? "text-rose-600" : "text-slate-600 dark:text-slate-300"}`}>{renderCurrencyValue(row.profit, true)}</td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.pno)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.orders)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.campaignCount)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.adCount)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    {totalOrders === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.domains.empty", {defaultValue: "Pro vybrané období nejsou dostupná doménová data."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}
